import {
  bbox,
  booleanDisjoint,
  buffer,
  distance,
  Feature,
  point,
  polygon,
  truncate,
  union,
} from '@turf/turf';
import { Point as XYPoint } from 'framer-motion';
import { MultiPolygon, Point, Polygon } from 'geojson';
import { parse, stringify } from 'wellknown';

import { Color } from '~/constants';
import { zoneStatusData } from '~/constants/zoneStatus';
import { I18n } from '~/i18n';
import { i18n } from 'i18next';
import {
  AlertCardData,
  AlertGroup,
  AlertPayLoadType,
  GroupedAlertDetails,
} from '~/pages/AlertsList/types';
import { CalendarMenu, TimeFilter } from '~/redux-rtk/slices/alertSlice';
import { mergeAndRemoveDuplicate, round } from '~/utils';
import { LngLat } from 'maplibre-gl';

// The distance (in kilometers) that we buffer our search polygon
const QUERY_BUFFER_KM = 12;

export const extractDateTime = (isoString: string) => {
  const date = new Date(isoString);

  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  // Format the date and time
  const formattedDate = `${month}-${day}-${year}`;
  const formattedTime = `${hours}:${minutes}:${seconds}`;

  return [formattedDate, formattedTime];
};

export const getNativeLanguageNames = languageCodes => {
  return languageCodes.map(code => {
    const displayNames = new Intl.DisplayNames([code], { type: 'language' });
    const displayName = displayNames.of(code);

    return {
      languageCode: code,
      displayName: displayName.charAt(0).toUpperCase() + displayName.slice(1),
    };
  });
};

export const getTranslatedText = (alertData, currentLanguage) => {
  if (alertData) {
    return (
      alertData?.find(data => data.id === currentLanguage)?.text ||
      alertData?.find(data => data.id === 'en')?.text
    );
  }
  return null;
};

export const getDefaultBrowserLanguage = () => {
  return (
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    'en'
  );
};

export const getAlertColor = (alertData: AlertCardData) => {
  if (!alertData?.active) {
    return Color.Grey600;
  }
  if (alertData?.source === 'EVAC') {
    return (
      zoneStatusData[alertData?.status?.id]?.color ??
      alertData?.status?.statusColor
    );
  }
  return Color.UniformBlue;
};

export const convertWktToGeoJson = wkt => {
  try {
    return parse(wkt);
  } catch (error) {
    return null;
  }
};

export const convertGeoJsonToWkt = geoJson => {
  try {
    return stringify(geoJson);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getDateFilters = (timeFilter: TimeFilter) => {
  switch (timeFilter.selectedMenu) {
    case CalendarMenu.today:
      return [
        {
          property: 'time',
          operator: 'eq',
          value: timeFilter?.fromDate,
        },
      ];
    case CalendarMenu.lastSevenDays:
      return [
        {
          property: 'time',
          operator: 'gte',
          value: timeFilter?.fromDate,
        },
      ];
    case CalendarMenu.lastThirtyDays:
      return [
        {
          property: 'time',
          operator: 'gte',
          value: timeFilter?.fromDate,
        },
      ];
    case CalendarMenu.customPeriod:
      return [
        {
          property: 'time',
          operator: 'gte',
          value: timeFilter?.fromDate,
        },
        {
          property: 'time',
          operator: 'lte',
          value: timeFilter?.toDate,
        },
      ];
  }
};

export const isZoneStatusCritical = status => {
  const statusObject = Object.values(zoneStatusData).find(
    item => item.status === status,
  );
  return statusObject?.critical ?? false;
};

// Create a buffered point to query with, to maximize caching
export const getBufferedPointForQuery = (location: [number, number]): string =>
  // We transform the search geometry in several ways:
  // 1. Round the lat / lon to the nearest 3 decimal places
  // 2. Buffer that point to 1000 meters (which is a safe amount more than the distance represented
  //    by 3 decimal places of coordinates)
  // 3. Convert that geojson to wkt
  convertGeoJsonToWkt(
    getBboxSquareAroundCoordinate({
      coordinate: { lon: round(location[0]), lat: round(location[1]) },
      bufferInKm: QUERY_BUFFER_KM,
    }),
  );

// We transform the search geometry in several ways:
// 1. Convert the rounded points to a polygon
// 2. Buffer that polygon
// 3. Truncate the precision to 1 decimal places
// 4. Convert that geojson to wkt
export const getBufferedPolygonForQuery = (extent: {
  xmax: number;
  ymax: number;
  xmin: number;
  ymin: number;
}) =>
  convertGeoJsonToWkt(
    truncate(
      buffer(
        polygon([
          [
            [round(extent.xmax), round(extent.ymax)],
            [round(extent.xmax), round(extent.ymin)],
            [round(extent.xmin), round(extent.ymin)],
            [round(extent.xmin), round(extent.ymax)],
            [round(extent.xmax), round(extent.ymax)],
          ],
        ]),
        QUERY_BUFFER_KM,
        { units: 'kilometers' },
      ),
      { coordinates: 2, precision: 1 },
    ),
  );

export const reSortResponseData = (
  data: AlertCardData[],
  initialQueryLocation: [number, number],
) => {
  // Create a point to sort on after the query
  const sortPoint = point(initialQueryLocation);
  // Return the data re-sorted based on the initial location
  return data?.sort?.(
    (a, b) =>
      distance(convertWktToGeoJson(a.centroid) as Point, sortPoint) -
      distance(convertWktToGeoJson(b.centroid) as Point, sortPoint),
  );
};

export const filterResponseData = (
  responseData: AlertCardData[],
  filterGeom: Feature<Polygon | Point>,
) => {
  return responseData?.filter(
    // Use the initial, unrounded, unbuffered search point as a polygon to filter on
    alert =>
      // booleanIntersects doesn't seem to be available, but disjoint is the exact opposite:
      // https://turfjs.org/docs/6.5.0/api/booleanDisjoint
      !booleanDisjoint(
        filterGeom,
        convertWktToGeoJson(alert.geom as MultiPolygon) as MultiPolygon,
      ),
  );
};

export const transformToAlertCardData = (
  alertGroup: AlertGroup,
): AlertCardData => {
  const groupedAlerts = alertGroup?.alerts?.reduce(
    (
      prevAlerts: {
        geom: MultiPolygon;
        zonesImpacted: string[];
      },
      currentAlert,
    ) => {
      if (!prevAlerts)
        return {
          zonesImpacted: currentAlert.zonesImpacted,
          geom: currentAlert.geom,
        };
      return {
        zonesImpacted: mergeAndRemoveDuplicate(
          prevAlerts.zonesImpacted,
          currentAlert.zonesImpacted,
        ),
        geom: convertGeoJsonToWkt(
          union(
            convertWktToGeoJson(prevAlerts.geom) as MultiPolygon,
            convertWktToGeoJson(currentAlert.geom) as MultiPolygon,
          ),
        ),
      };
    },
    null,
  );

  // Start with using all of the properties from the first alert as "base" properties, but then
  // allow the processed groupedAlerts to override that as necessary, and add the id
  return {
    ...alertGroup?.alerts[0],
    ...groupedAlerts,
    id: alertGroup?.id_event_incident,
    titles:
      alertGroup?.alerts[0].source === 'ALERT'
        ? // If it's an ALERT record, just use the titles plainly
        alertGroup.alerts[0].titles
        : [
          // Otherwise we just have one english "title" that we manually create for EVAC zone
          // status records
          {
            id: 'en',
            text: `${alertGroup.alerts.length > 1
              ? `${I18n.t('alertsList.multipleZones')} ` // Add in the "multiple zones" as needed
              : ''
              }${alertGroup?.alerts[0].status.description}`,
          },
        ],
  };
};

export const mergeRefs = (muiRef: any, customRef: React.Ref<any>) => (el: any) => {
  if (typeof muiRef === 'function') {
    muiRef(el);
  } else if (muiRef && typeof muiRef === 'object') {
    muiRef.current = el;
  }

  if (typeof customRef === 'function') {
    customRef(el);
  } else if (customRef && typeof customRef === 'object') {
    // @ts-expect-error
    customRef.current = el;
  }
};

export const getBboxSquareAroundCoordinate = ({
  coordinate,
  bufferInKm = QUERY_BUFFER_KM,
}: {
  coordinate: { lat: number; lon: number };
  bufferInKm?: number;
}): [number, number, number, number] => {
  // Create a Turf point
  const locationCoordinates = point([coordinate.lon, coordinate.lat]);

  // Create a buffer around the point
  const buffered = buffer(locationCoordinates, bufferInKm, {
    units: 'kilometers',
  });

  // Get the bounding box of the buffered area
  return bbox(buffered) as [number, number, number, number];
};

export const filterEvacAlertsByGeometry = (
  responseData,
  filterGeom: Feature<Polygon | Point>,
) => {
  return responseData?.filter(
    // Use the initial, unrounded, unbuffered search point as a polygon to filter on
    alert =>
      // booleanIntersects doesn't seem to be available, but disjoint is the exact opposite:
      // https://turfjs.org/docs/6.5.0/api/booleanDisjoint
      !booleanDisjoint(filterGeom, JSON.parse(alert.zonegeom)),
  );
};

export const processResponse = (
  response: { data: GroupedAlertDetails },
  params: AlertPayLoadType,
  coordinates: LngLat,
): AlertCardData[] => {
  let processedData: AlertCardData[] = [];

  if (params?.groupedAlerts) {
    //if we are calling alertslist/groupincident then -
    // - we should process the `GroupAlertDetails` type to `AlertCardData[]`
    processedData = (response?.data as GroupedAlertDetails)?.content?.map(
      alertGroup => transformToAlertCardData(alertGroup),
    );
  } else {
    processedData = (response?.data as GroupedAlertDetails)
      ?.content as AlertCardData[];
  }
  // If we searched for geometry
  if (params.filters['searchGeom']) {
    // Filter out unmatching items based on the actual searched geometry, since we reduced the
    // precision when we sent the network request
    processedData = filterResponseData(
      processedData,
      polygon([
        [
          [
            params.filters['searchGeom'].xmax,
            params.filters['searchGeom'].ymax,
          ],
          [
            params.filters['searchGeom'].xmax,
            params.filters['searchGeom'].ymin,
          ],
          [
            params.filters['searchGeom'].xmin,
            params.filters['searchGeom'].ymin,
          ],
          [
            params.filters['searchGeom'].xmin,
            params.filters['searchGeom'].ymax,
          ],
          [
            params.filters['searchGeom'].xmax,
            params.filters['searchGeom'].ymax,
          ],
        ],
      ]),
    );
  }
  // If we're sorting based on the user's location, we reduced that precision, so need to re
  // sort it again
  if (coordinates?.lat) {
    // Sort the response data based on distance to that point
    processedData = reSortResponseData(processedData, [
      coordinates.lng,
      coordinates?.lat,
    ]);
  }
  return processedData;
};

export const getAlertSubtitle = (alertData: AlertCardData, _i18n: i18n) =>
  alertData?.source === 'ALERT'
    ? getTranslatedText(alertData?.descriptions, _i18n.language)
    : alertData?.statusReason;
